import {
  ButtonDS,
  dateFormatterDayMonthYearLong2Digits,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  triggerToast,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "../../i18n/i18n";
import {
  cardsSlice,
  selectCardsList,
  selectCardsListStatus,
  selectGenerateShipmentStatus,
} from "./cardsSlice";
import { CardsList, CardsListDisplayed } from "./cardsApi";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { formatDate, getDate } from "../../lib/materialUiFormats";
import { ShipmentModal } from "./modals/shipmentModal";
import { selectCompaniesList } from "../companiesSlice";

export const CardsTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const cardsList = useAppSelector(selectCardsList);
  const cardsListStatus = useAppSelector(selectCardsListStatus);
  const generateShipmentStatus = useAppSelector(selectGenerateShipmentStatus);
  const [listDisplayed, setListDisplayed] = useState<CardsListDisplayed[]>([]);
  const [rowsChecked, setRowsChecked] = useState<CardsList[]>([]);
  const [shipmentModalVisible, setShipmentModalVisible] =
    useState<boolean>(false);
  const companiesList = useAppSelector(selectCompaniesList);

  const cardsListWithoutShipments = useMemo(
    () => cardsList.filter((card) => card.shipment === null),
    [cardsList],
  );

  const companyUuid = useMemo(() => params.id ?? "", [params.id]);
  const companyName = useMemo(
    () =>
      companiesList.find((company) => company.uuid === companyUuid)?.name ?? "",
    [companyUuid, companiesList],
  );

  const pageName = "cards_shipments";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      registrationNumber: "size",
      vehicleReference: "size",
      rfid: "size",
      status: "size",
      address: "size",
      createdAt: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof CardsList, value: string) => {
      switch (key) {
        case "status":
          return t(`cards.status.${value}`);
        case "createdAt":
          return dateFormatterDayMonthYearLong2Digits(new Date(value));
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () => ["status", "createdAt"] as (keyof CardsListDisplayed)[],
    [],
  );

  const cardsListWithId: (CardsList & { id: string })[] = useMemo(() => {
    const rowsWithId = cardsListWithoutShipments.map((card) => {
      let newDict: CardsList & {
        id: string;
      } = {
        ...card,
        id: card.uuid,
      };
      Object.entries(card).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof CardsListDisplayed)) {
          newDict = {
            ...newDict,
            [key]: findKeysToTranslate(
              key as keyof CardsListDisplayed,
              value as string,
            ),
          };
        }
      });
      return newDict;
    });
    return rowsWithId;
  }, [cardsListWithoutShipments, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (
      cardsListWithId &&
      cardsListWithoutShipments &&
      cardsListStatus === "success"
    ) {
      setListDisplayed(cardsListWithId);
    }
  }, [cardsListWithId, cardsListWithoutShipments, cardsListStatus]);

  useEffect(() => {
    if (generateShipmentStatus === "success") {
      triggerToast(t("cards_shipments.upload.success") || "", "valid");
      dispatch(cardsSlice.actions.resetGenerateShipmentStatus());
    } else if (generateShipmentStatus === "failed") {
      triggerToast(t("cards_shipments.upload.failure") || "", "error");
      dispatch(cardsSlice.actions.resetGenerateShipmentStatus());
    }
  }, [generateShipmentStatus, t, dispatch]);

  const searchBarProps = {
    values: cardsListWithId,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  const columnsDisplayed: {
    [index in keyof CardsListDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      registrationNumber: {
        type: "string",
        width: 160,
      },
      vehicleReference: {
        type: "string",
        width: 200,
      },
      identifier: {
        type: "string",
        width: 150,
      },
      rfid: {
        type: "string",
        width: 130,
      },
      last4Digits: {
        type: "string",
        width: 130,
      },
      status: {
        type: "string",
        width: 150,
      },
      createdAt: {
        type: "date",
        width: 150,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      addressDisplayed: {
        type: "string",
        width: 600,
      },
    };
  }, []);

  const isGenerateShipmentButtonDisabled = useMemo(
    () => rowsChecked.length === 0,
    [rowsChecked],
  );

  const resetCheckedCards = useCallback(() => {
    setRowsChecked([]);
  }, []);

  const handleSetRowsChecked = (rowsCheckedDisplayed: CardsListDisplayed[]) => {
    const cardsChecked = cardsListWithId.filter((row) =>
      rowsCheckedDisplayed.some((r) => "id" in r && r["id"] === row.id),
    );
    setRowsChecked(cardsChecked);
  };

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (params.tab !== "cards") {
    return;
  }

  return (
    <StyledContainer>
      <Spacer y={1} />
      <div>
        <ButtonDS
          text={t("cards_shipments.button.create")}
          onClick={() =>
            !isGenerateShipmentButtonDisabled && setShipmentModalVisible(true)
          }
          format="hug"
          buttonType="primary"
          disabled={isGenerateShipmentButtonDisabled}
        />
      </div>
      <Spacer y={1} />
      <MaterialUiTable<keyof CardsListDisplayed, CardsListDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        rowsChecked={rowsChecked}
        setRowsChecked={(rows) => handleSetRowsChecked(rows)}
      />
      <Spacer y={2} />
      <ShipmentModal
        visible={shipmentModalVisible}
        onCloseModal={() => {
          setShipmentModalVisible(false);
        }}
        onSuccess={() => {
          resetCheckedCards();
        }}
        cardsToShip={rowsChecked}
        companyUuid={companyUuid}
        companyName={companyName}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
